import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Circle, Square, Trash2, CheckCircle2, Mic, MicOff } from 'lucide-react';
import Dialog from './ui/Dialog';
import Field, { FormErrorSummary, FormServerError, useFormValidation, type FormSchema } from './ui/Field';
import { FormFooter } from './projects/formBits';
import { api } from '../services/api';
import { toast } from '../store/toastStore';
import { getCapture, type SessionRecordingInstance, type SessionRecording } from '../modules/bug-report/capture';
import type { CreateBugReportPayload, BugReportKind } from '../types';
import { useVoiceCapture } from './bugReport/useVoiceCapture';
import type { Region } from './bugReport/RegionSelector';

interface Props {
  onClose: () => void;
  /** D-341: 'suggestion' = an improvement idea: no screenshot, no technical context, no session recording. */
  kind?: BugReportKind;
  /** D-323: the whole-page screenshot. */
  fullScreenshot?: Blob | null;
  /** D-323: the same screenshot with a red frame on the marked area (the full one when no area was marked). */
  markedScreenshot?: Blob | null;
  region?: Region | null;
}

// D-323: a description OR a voice note. The "description" id is the one field the validator checks;
// the audio case is checked in `submit`.
const BUG_SCHEMA: FormSchema = {};

/** The entity the user is looking at, from the URL (ticket / customer / project / lead / meeting). */
function currentEntity(): { entity_type?: string; entity_id?: string } {
  const m = window.location.pathname.match(/^\/(tickets|organizations|projects|leads|meetings)\/([0-9a-f-]{36})/i);
  if (!m) return {};
  const type = ({ tickets: 'ticket', organizations: 'customer', projects: 'project', leads: 'lead', meetings: 'meeting' } as Record<string, string>)[m[1]];
  return { entity_type: type, entity_id: m[2] };
}

export default function BugReportModal({ onClose, kind = 'bug', fullScreenshot, markedScreenshot, region }: Props) {
  const { t } = useTranslation();
  const isSuggestion = kind === 'suggestion';
  const navigate = useNavigate();

  const [description, setDescription] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [actualBehavior, setActualBehavior] = useState('');
  const [needText, setNeedText] = useState(false);

  const [preview, setPreview] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const recorderRef = useRef<SessionRecordingInstance | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recording, setRecording] = useState<SessionRecording | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const voice = useVoiceCapture((text) => setDescription((d) => (d ? `${d} ${text}` : text)));

  useEffect(() => {
    const blob = markedScreenshot ?? fullScreenshot;
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [markedScreenshot, fullScreenshot]);

  useEffect(() => {
    if (!voice.audio) { setAudioUrl(null); return; }
    const url = URL.createObjectURL(voice.audio);
    setAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [voice.audio]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (recorderRef.current?.isRecording()) recorderRef.current.stop();
    };
  }, []);

  const handleStartRecording = useCallback(() => {
    const capture = getCapture();
    if (!capture) return;
    const instance = capture.startRecording({ maxDurationMs: 120_000 });
    recorderRef.current = instance;
    setIsRecording(true);
    setRecordingDuration(0);
    timerRef.current = setInterval(() => {
      if (instance.isRecording()) {
        setRecordingDuration(Math.floor(instance.getDurationMs() / 1000));
      } else {
        if (timerRef.current) clearInterval(timerRef.current);
        setIsRecording(false);
        setRecording(instance.stop());
      }
    }, 1000);
  }, []);

  const handleStopRecording = useCallback(() => {
    if (recorderRef.current?.isRecording()) {
      const result = recorderRef.current.stop();
      setRecording(result);
    }
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleRemoveRecording = useCallback(() => {
    setRecording(null);
    setRecordingDuration(0);
  }, []);

  const submitMutation = useMutation({
    mutationFn: async () => {
      const capture = isSuggestion ? null : getCapture();
      const snapshot = capture?.getSnapshot() ?? {};

      const payload: CreateBugReportPayload = {
        report_kind: kind,
        description: description.trim() || undefined,
        has_audio: !!voice.audio,
        region: region ?? null,
        page_url: window.location.href,
        page_title: document.title,
        ...currentEntity(),
        steps_to_reproduce: stepsToReproduce || undefined,
        expected_behavior: expectedBehavior || undefined,
        actual_behavior: actualBehavior || undefined,
        ...(isSuggestion ? {} : { capture_snapshot: snapshot as Record<string, any> }),
      };

      const result = await api.createBugReport(payload);
      const ticketId = result.data?.ticket?.id;

      if (ticketId) {
        // The marked area and the full screenshot; a failed attachment must not lose the report itself.
        if (markedScreenshot) await api.uploadBugReportScreenshot(ticketId, markedScreenshot).catch(() => undefined);
        if (fullScreenshot && region) await api.uploadBugReportScreenshot(ticketId, fullScreenshot, 'full').catch(() => undefined);
        if (voice.audio) await api.uploadBugReportAudio(ticketId, voice.audio).catch(() => undefined);
        if (recording) {
          await api.uploadBugReportRecording(ticketId, {
            events: recording.events,
            startedAt: recording.startedAt,
            duration_ms: recording.duration_ms,
            viewport: recording.viewport,
          }).catch(() => undefined);
        }
      }

      return result;
    },
    onSuccess: () => {
      // DQ-086: the dialog closed with no word - the reporter could not tell it was sent.
      // FIX-D323-F1: the toast names the place AND takes her there (avatar menu > "הדיווחים שלי" was easy to miss).
      toast('success', t(isSuggestion ? 'bugFlow.sentSuggestion' : 'bugFlow.sent'), { action: { label: t('bugFlow.openMyReports'), onClick: () => navigate('/my-bug-reports') } });
      onClose();
    },
  });

  const v = useFormValidation(BUG_SCHEMA, {});

  const send = v.submit(() => {
    if (!description.trim() && !voice.audio) { setNeedText(true); return; }
    setNeedText(false);
    submitMutation.mutate();
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(isSuggestion ? 'bugFlow.suggestionTitle' : 'bugFlow.title')}
      size="lg"
      dismissible={!submitMutation.isPending}
      footer={(
        <FormFooter
          formId="bug-report-form"
          busy={submitMutation.isPending}
          submitLabel={t('bugFlow.send')}
          onCancel={onClose}
        />
      )}
    >
      <form id="bug-report-form" noValidate className="space-y-4" onSubmit={send}>
        {v.showSummary && <FormErrorSummary errors={v.errors} labels={{}} order={[]} />}
        <FormServerError error={submitMutation.error} fallback={t('bugReport.submitError')} />

        {!isSuggestion && preview && (
          <div className="flex items-center gap-3 rounded-lg border border-border bg-surface-subtle p-2">
            <img src={preview} alt="" className="h-20 w-32 rounded-xs border object-cover object-top" data-testid="bug-preview" />
            <span className="text-sm text-text-2">{region ? t('bugFlow.areaMarked') : t('bugFlow.fullScreen')}</span>
          </div>
        )}

        <Field id="bug-description" label={t(isSuggestion ? 'bugFlow.suggestionDescLabel' : 'bugFlow.descLabel')} error={needText ? t('bugFlow.needText') : undefined}>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t(isSuggestion ? 'bugFlow.suggestionPlaceholder' : 'bugFlow.descPlaceholder')}
            className="input w-full min-h-28 resize-y"
            maxLength={5000}
            data-testid="bug-description"
          />
        </Field>

        <div className="flex flex-wrap items-center gap-2" data-testid="bug-voice-row">
          {voice.dictating ? (
            <button type="button" className="btn btn-secondary border-danger-300 text-danger-700" onClick={voice.stopDictation}>
              <MicOff size={16} aria-hidden="true" /> {t('bugFlow.dictateStop')} <span className="text-xs">({t('bugFlow.dictating')})</span>
            </button>
          ) : (
            <button type="button" className="btn btn-secondary" onClick={() => { void voice.startDictation(); }} disabled={voice.recording} data-testid="bug-dictate">
              <Mic size={16} aria-hidden="true" /> {t('bugFlow.dictate')}
            </button>
          )}
          {voice.recording && !voice.dictating ? (
            <button type="button" className="btn btn-secondary border-danger-300 text-danger-700" onClick={voice.stopRecording}>
              <Square size={14} className="fill-current" aria-hidden="true" /> {t('bugFlow.recordStop')} <span className="text-xs">({t('bugFlow.recording')})</span>
            </button>
          ) : !voice.dictating && (
            <button type="button" className="btn btn-secondary" onClick={() => { void voice.startRecording(); }} data-testid="bug-record-audio">
              <Circle size={14} className="text-danger-600" aria-hidden="true" /> {t('bugFlow.recordAudio')}
            </button>
          )}
          {voice.audio && audioUrl && (
            <div className="flex items-center gap-2 rounded-lg border border-success-200 bg-success-50 px-3 py-1.5 text-sm">
              <CheckCircle2 size={14} className="text-success-600" aria-hidden="true" />
              <span className="text-success-700">{t('bugFlow.audioAttached')}</span>
              <audio src={audioUrl} controls className="h-8"><track kind="captions" /></audio>
              <button type="button" onClick={voice.clearAudio} aria-label={t('bugFlow.removeAudio')} className="btn-icon text-text-3 hover:text-danger-700">
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
        {voice.error && (
          <p role="alert" className="text-sm text-danger-700">
            {voice.error === 'denied' ? t('bugFlow.micDenied') : t('bugFlow.dictationUnavailable')}
          </p>
        )}

        {/* The previous optional detail fields stay, collapsed (D-323: nothing is mandatory but the description). */}
        {!isSuggestion && (<details className="rounded-lg border border-border px-3 py-2">
          <summary className="cursor-pointer text-sm text-text-2">{t('bugReport.stepsToReproduce')} / {t('bugReport.expectedBehavior')} / {t('bugReport.actualBehavior')}</summary>
          <div className="mt-3 space-y-3">
            <Field id="bug-steps" label={t('bugReport.stepsToReproduce')}>
              <textarea
                value={stepsToReproduce}
                onChange={(e) => setStepsToReproduce(e.target.value)}
                placeholder={t('bugReport.stepsPlaceholder')}
                className="input w-full min-h-16 resize-y"
                maxLength={5000}
              />
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field id="bug-expected" label={t('bugReport.expectedBehavior')}>
                <textarea
                  value={expectedBehavior}
                  onChange={(e) => setExpectedBehavior(e.target.value)}
                  placeholder={t('bugReport.expectedPlaceholder')}
                  className="input w-full min-h-16 resize-y"
                  maxLength={3000}
                />
              </Field>
              <Field id="bug-actual" label={t('bugReport.actualBehavior')}>
                <textarea
                  value={actualBehavior}
                  onChange={(e) => setActualBehavior(e.target.value)}
                  placeholder={t('bugReport.actualPlaceholder')}
                  className="input w-full min-h-16 resize-y"
                  maxLength={3000}
                />
              </Field>
            </div>
          </div>
        </details>)}

        {/* Session recording (existing) */}
        {!isSuggestion && (<div className="flex flex-wrap gap-3">
          {recording ? (
            <div className="flex items-center gap-2 rounded-lg border border-success-200 bg-success-50 px-3 py-2 text-sm">
              <CheckCircle2 size={14} className="text-success-600" aria-hidden="true" />
              <span className="text-success-700">
                {t('bugReport.recordingCaptured')} ({recording.events.length} {t('bugReport.events')}, {Math.round(recording.duration_ms / 1000)}{t('bugReport.duration_seconds')})
              </span>
              <button type="button" onClick={handleRemoveRecording} aria-label={t('bugReport.removeRecording')} className="btn-icon text-text-3 hover:text-danger-700">
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </div>
          ) : isRecording ? (
            <button
              type="button"
              onClick={handleStopRecording}
              className="btn btn-secondary border-danger-300 text-danger-700"
            >
              <Square size={14} className="fill-current" aria-hidden="true" />
              {t('bugReport.stopRecordingAction')} (<span className="tabular-nums">{recordingDuration}</span>{t('bugReport.duration_seconds')})
            </button>
          ) : (
            <button type="button" onClick={handleStartRecording} className="btn btn-secondary">
              <Circle size={16} className="text-danger-600" aria-hidden="true" />
              {t('bugReport.startRecordingAction')}
            </button>
          )}
        </div>)}

        {!isSuggestion && <p className="flex items-center gap-1.5 text-xs text-text-2">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-success-500" aria-hidden="true" />
          {t('bugFlow.autoSaved')}
        </p>}
      </form>
    </Dialog>
  );
}
